import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { differenceInCalendarDays, format, isToday, isTomorrow, parseISO } from "date-fns";
import { CheckCircle2, ChevronDown, ListTodo, Phone, RotateCcw, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { fromTaskState } from "@/lib/taskReturn";
import type { TaskRead, TaskType } from "@/api/types";

const ICONS: Record<TaskType, typeof Phone> = {
  call: Phone,
  meeting: Users,
  follow_up: RotateCcw,
  task: ListTodo,
};

const TYPE_LABELS: Record<TaskType, string> = {
  call: "Call",
  meeting: "Meeting",
  follow_up: "Follow-up",
  task: "Task",
};

interface TodoCardProps {
  title: string;
  tasks: TaskRead[];
  slug: string;
  /** Rows shown before "Show more". */
  rows?: number;
  showProject?: boolean;
  empty: { title: string; hint: string };
  /** Next to the title: a filter chip, a link. */
  aside?: ReactNode;
  id?: string;
}

/** A worklist after the owner's nidria front: 52px rows on a white plane, an icon tile
 *  per task type (rose when overdue), the company first, the task under it, the lateness
 *  as a pill, and an Open button. */
export function TodoCard({
  title,
  tasks,
  slug,
  rows = 6,
  showProject = false,
  empty,
  aside,
  id,
}: TodoCardProps) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? tasks : tasks.slice(0, rows);
  const hidden = tasks.length - rows;

  return (
    <section id={id} className="surface-plane scroll-mt-24 p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="type-zonehead">{title}</h2>
        {aside}
      </div>

      {tasks.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          </span>
          <p className="text-sm font-medium text-slate-700">{empty.title}</p>
          <p className="text-xs text-slate-400">{empty.hint}</p>
        </div>
      ) : (
        <ul className="mt-3">
          {visible.map((task, i) => (
            <TodoRow
              key={task.id}
              task={task}
              slug={slug}
              first={i === 0}
              showProject={showProject}
            />
          ))}
        </ul>
      )}

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-3 inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[13px] text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800"
        >
          {expanded ? "Show less" : `Show more (${hidden})`}
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")} />
        </button>
      )}
    </section>
  );
}

function TodoRow({
  task,
  slug,
  first,
  showProject,
}: {
  task: TaskRead;
  slug: string;
  first: boolean;
  showProject: boolean;
}) {
  // Cross-project rows carry their own project: link and label to that one.
  const taskSlug = (task as { project_slug?: string }).project_slug ?? slug;
  const projectName = (task as { project_name?: string }).project_name;
  const to = task.company_id
    ? `/projects/${taskSlug}/companies/${task.company_id}`
    : `/projects/${taskSlug}/tasks`;
  const type = (task.task_type ?? "task") as TaskType;
  const Icon = ICONS[type] ?? ListTodo;
  const late = task.is_overdue && task.due_at ? lateness(task.due_at) : null;

  return (
    <li className={cn(!first && "border-t border-slate-900/[0.05]")}>
      <Link
        to={to}
        // The company page then leads back here (Prism task #270).
        state={task.company_id ? fromTaskState(task, "Dashboard") : undefined}
        className="group flex h-[52px] items-center gap-3 rounded-[10px] px-3 transition-colors duration-150 hover:bg-indigo-500/[0.04]"
      >
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            task.is_overdue ? "bg-rose-50 text-rose-500" : "bg-indigo-500/[0.08] text-indigo-600",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium leading-5 text-slate-900">
            {task.company_name || task.title}
          </span>
          <span className="block truncate text-[12.5px] leading-4 text-slate-500">
            {task.company_name ? withoutCompany(task.title, task.company_name) || TYPE_LABELS[type] : TYPE_LABELS[type]}
            {showProject && projectName ? ` · ${projectName}` : ""}
          </span>
        </span>
        {late && (
          <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-rose-600">
            {late}
          </span>
        )}
        {task.due_at && !task.is_overdue && (
          <span className="hidden shrink-0 text-xs tabular-nums text-slate-400 sm:block">
            {dueLabel(task.due_at)}
          </span>
        )}
        <span className="h-7 shrink-0 whitespace-nowrap rounded-lg bg-indigo-500/[0.08] px-3 text-xs font-medium leading-7 text-indigo-700 transition-colors duration-150 group-hover:bg-indigo-600 group-hover:text-white">
          Open
        </span>
      </Link>
    </li>
  );
}

/** The task's title without its company, already shown above it: "ACME - Jane Doe" and
 *  "Follow up with Jane Doe — ACME" read "Jane Doe" and "Follow up with Jane Doe". */
function withoutCompany(title: string, company: string): string {
  const name = company.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return title
    .replace(new RegExp(`^\\s*${name}\\s*[-—–:·|]\\s*`, "i"), "")
    .replace(new RegExp(`\\s*[-—–:·|]\\s*${name}\\s*$`, "i"), "")
    .trim();
}

/** "+18d" — how late, in days; "Late" within the day. */
function lateness(iso: string): string {
  const days = differenceInCalendarDays(new Date(), parseISO(iso));
  return days >= 1 ? `+${days}d` : "Late";
}

function dueLabel(iso: string): string {
  const d = parseISO(iso);
  if (isToday(d)) return `Today ${format(d, "HH:mm")}`;
  if (isTomorrow(d)) return `Tomorrow ${format(d, "HH:mm")}`;
  return format(d, "EEE d MMM");
}
